import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { ApiError, account, auth } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Feedback } from "./Feedback";
import { LoginDialog } from "./LoginDialog";
import { createScene, type Season } from "./scenes";
import { useBodyClass } from "./useBodyClass";
import "../../styles/legacy/LoginView.css";

type Popup = null | "lost" | "contact";

/** Which backdrop the login page shows. Switched by hand, as the old site did. */
export const SEASON: Season = "fall";

export function LoginPage() {
  useBodyClass("login-page");
  useBodyClass(`season-${SEASON}`);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tab, setTab] = useState<"login" | "new">("login");
  const [popup, setPopup] = useState<Popup>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== "function" || !canvas.getContext("2d")) return;
    const scene = createScene(SEASON, canvas);
    scene.start().catch(() => {});
    return () => scene.stop();
  }, []);

  return (
    <div className="LoginView">
      <canvas className="backgroundCanvas" ref={canvasRef} />
      <div className="links">
        <div className="iosButton">
          <a href="https://itunes.apple.com/us/app/geargrams/id1026942521?ls=1&mt=8">
            <img width={120} src="/assets/ui/mobilead/iTunesBTN.png" alt="Download on the App Store" />
          </a>
        </div>
        <div>
          <a href="#lost" onClick={(e) => (e.preventDefault(), setPopup("lost"))}>
            Lost Password
          </a>
        </div>
        <div>
          <a href="#contact" onClick={(e) => (e.preventDefault(), setPopup("contact"))}>
            Contact
          </a>
        </div>
      </div>

      <div className="mainForm" style={popup ? { visibility: "hidden" } : undefined}>
        <img className="logo" src="/assets/ui/gg_logo.png" alt="GearGrams" />
        <div className="tabs">
          <div className={tab === "login" ? "selected" : ""} onClick={() => setTab("login")}>
            Login
          </div>
          <div className={tab === "new" ? "selected" : ""} onClick={() => setTab("new")}>
            New Account
          </div>
        </div>
        {tab === "login" ? <LoginForm /> : <CreateAccountForm />}
      </div>


      {popup === "lost" && <LostPasswordPopup onClose={() => setPopup(null)} />}
      {popup === "contact" && <ContactPopup onClose={() => setPopup(null)} />}
    </div>
  );
}

function LoginForm() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [feedback, setFeedback] = useState<{ text: string; top: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const userRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);

  const fieldTop = (el: HTMLElement | null, offset = 0) => (el?.offsetTop ?? 0) + 7 + offset;

  async function onLogin(e?: FormEvent) {
    e?.preventDefault();
    if (!username) return setFeedback({ text: "Username or Email", top: fieldTop(userRef.current) });
    if (!password) return setFeedback({ text: "Password", top: fieldTop(passRef.current) });
    setBusy(true);
    try {
      const res = await auth.login(username, password);
      setUser(res.user);
      navigate("/library");
    } catch (err) {
      const text = err instanceof ApiError && err.status === 429 ? "Too many attempts, try again later" : "Username or password is incorrect";
      setFeedback({ text, top: fieldTop(passRef.current, -24) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form name="login-form" autoComplete="off" className="loginControls" onSubmit={onLogin}>
      <Feedback text={feedback?.text ?? null} top={feedback?.top ?? 0} />
      <div>
        <input
          ref={userRef}
          autoComplete="off"
          type="text"
          spellCheck={false}
          name="username"
          placeholder="USERNAME or EMAIL"
          aria-label="Username or email"
          value={username}
          onChange={(e) => (setUsername(e.target.value), setFeedback(null))}
        />
      </div>
      <div>
        <input
          ref={passRef}
          type="password"
          name="password"
          placeholder="PASSWORD"
          aria-label="Password"
          value={password}
          onChange={(e) => (setPassword(e.target.value), setFeedback(null))}
        />
      </div>
      <div>
        <a href="#signin" onClick={(e) => (e.preventDefault(), onLogin())} className="button loginButton" aria-disabled={busy}>
          SIGN IN
        </a>
      </div>
      <input type="submit" style={{ position: "absolute", visibility: "hidden", marginTop: -5000 }} />
    </form>
  );
}

function CreateAccountForm() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [feedback, setFeedback] = useState<{ text: string; top: number } | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const p1Ref = useRef<HTMLInputElement>(null);
  const p2Ref = useRef<HTMLInputElement>(null);
  const fieldTop = (el: HTMLElement | null) => (el?.offsetTop ?? 0) + 7;

  async function onCreate(e?: FormEvent) {
    e?.preventDefault();
    if (!email.includes("@")) return setFeedback({ text: "Email is invalid", top: fieldTop(emailRef.current) });
    if (p1.length < 8) return setFeedback({ text: "At least 8 characters", top: fieldTop(p1Ref.current) });
    if (p1 !== p2) return setFeedback({ text: "Passwords don't match", top: fieldTop(p2Ref.current) });
    try {
      const res = await auth.signup(email, p1);
      setUser(res.user);
      navigate("/library");
    } catch (err) {
      const text = err instanceof ApiError ? err.message : "Could not create the account";
      setFeedback({ text, top: fieldTop(emailRef.current) });
    }
  }

  return (
    <form className="CreateAccountView" style={{ display: "block" }} onSubmit={onCreate}>
      <Feedback text={feedback?.text ?? null} top={feedback?.top ?? 0} />
      <div>
        <input ref={emailRef} spellCheck={false} type="text" name="emailNew" placeholder="EMAIL" aria-label="Email" value={email} onChange={(e) => (setEmail(e.target.value), setFeedback(null))} />
      </div>
      <div>
        <input ref={p1Ref} type="password" name="password" placeholder="PASSWORD" aria-label="New password" value={p1} onChange={(e) => (setP1(e.target.value), setFeedback(null))} />
      </div>
      <div>
        <input ref={p2Ref} type="password" name="password2" placeholder="RETYPE PASSWORD" aria-label="Retype password" value={p2} onChange={(e) => (setP2(e.target.value), setFeedback(null))} />
      </div>
      <div>
        <a href="#create" onClick={(e) => (e.preventDefault(), onCreate())} className="button loginButton">
          CREATE ACCOUNT
        </a>
      </div>
      <input type="submit" style={{ position: "absolute", visibility: "hidden", marginTop: -5000 }} />
    </form>
  );
}

function LostPasswordPopup({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<{ text: string; error?: boolean } | null>(null);
  const [sent, setSent] = useState(false);
  async function onRecover(e?: FormEvent) {
    e?.preventDefault();
    if (!email.includes("@")) return setStatus({ text: "Please enter the email address of your account.", error: true });
    await auth.forgot(email).catch(() => {});
    setSent(true);
    setStatus({ text: "If that address has an account, an email with a reset link is on its way. Check your spam folder too." });
  }
  return (
    <LoginDialog title="Lost password?" intro="No problem. Enter the email address of your account and we'll send you a link to choose a new password." onClose={onClose}>
      <form onSubmit={onRecover}>
        <label htmlFor="lost-email">Email address</label>
        <input id="lost-email" type="text" placeholder="you@example.com" autoFocus value={email} onChange={(e) => (setEmail(e.target.value), setStatus(null))} />
        <div className={`status${status?.error ? " error" : ""}`} role="status">
          {status?.text}
        </div>
        <div className="actions">
          <button type="button" className="secondary" onClick={onClose}>
            {sent ? "Close" : "Cancel"}
          </button>
          {!sent && <button type="submit">Send reset link</button>}
        </div>
      </form>
    </LoginDialog>
  );
}

function ContactPopup({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<{ text: string; error?: boolean } | null>(null);
  const [sent, setSent] = useState(false);
  async function onContact(e?: FormEvent) {
    e?.preventDefault();
    if (!email.includes("@")) return setStatus({ text: "Please enter your email so we can reply.", error: true });
    if (!message.trim()) return setStatus({ text: "Please write a message.", error: true });
    try {
      await account.support({ name, from_email: email, subject: "Contact form", message });
      setSent(true);
      setStatus({ text: "Thanks, your message has been sent." });
    } catch (err) {
      setStatus({ text: err instanceof ApiError ? err.message : "Could not send the message.", error: true });
    }
  }
  return (
    <LoginDialog
      title="Contact"
      intro={
        <>
          Fill out the form, or email <a href="mailto:help@geargrams.com">help@geargrams.com</a>.
        </>
      }
      onClose={onClose}
    >
      <form onSubmit={onContact}>
        <label htmlFor="contact-name">Name</label>
        <input id="contact-name" type="text" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        <label htmlFor="contact-email">Email</label>
        <input id="contact-email" type="text" placeholder="you@example.com" value={email} onChange={(e) => (setEmail(e.target.value), setStatus(null))} />
        <label htmlFor="contact-message">Message</label>
        <textarea id="contact-message" value={message} onChange={(e) => (setMessage(e.target.value), setStatus(null))} />
        <div className={`status${status?.error ? " error" : ""}`} role="status">
          {status?.text}
        </div>
        <div className="actions">
          <button type="button" className="secondary" onClick={onClose}>
            {sent ? "Close" : "Cancel"}
          </button>
          {!sent && <button type="submit">Send</button>}
        </div>
      </form>
    </LoginDialog>
  );
}
